import React, { Suspense, lazy, useEffect, useRef } from 'react';
import {
    Navigate,
    Route,
    Routes,
    useLocation,
    useNavigationType,
    // withRouter
} from 'react-router-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Page from './Page';
import Spinner from '../layout/Spinner';
// import EditProfile from '../profile-forms/EditProfile';
// import AddExperience from '../profile-forms/AddExperience';
// import AddEducation from '../profile-forms/AddEducation';
// import EditMyArtistBooking from '../artists/EditMyArtistBooking';
// import Posts from '../posts/Posts';
// import Post from '../post/Post';
import PrivateRoute from '../routing/PrivateRoute';
import AdminPrivateRoute from '../routing/AdminPrivateRoute';
import BookingPrivateRoute from '../routing/BookingPrivateRoute';
import ArtistPrivateRoute from '../routing/ArtistPrivateRoute';

import { useTransition, animated, useSpring } from '@react-spring/web';

const Landing = lazy(() => import('../layout/Landing'));
const Register = lazy(() => import('../auth/Register'));
const Login = lazy(() => import('../auth/Login'));
const Dashboard = lazy(() => import('../dashboard/Dashboard'));
const CreateProfile = lazy(() => import('../profile-forms/CreateProfile'));
const Profiles = lazy(() => import('../profiles/Profiles'));
const Profile = lazy(() => import('../profile/Profile'));
const Artists = lazy(() => import('../artists/Artists'));
const Artist = lazy(() => import('../artists/Artist'));
const EditArtists = lazy(() => import('../artists/EditArtists'));
const EditUsers = lazy(() => import('../users/EditUsers'));
const EditMyArtistProfile = lazy(
    () => import('../artists/EditMyArtistProfile')
);
const EditMyHostProfile = lazy(() => import('../hosts/EditMyHostProfile'));
const EditMyAttendeeProfile = lazy(
    () => import('../attendees/EditMyAttendeeProfile')
);
const UnsubscribeHostDigest = lazy(
    () => import('../hosts/UnsubscribeHostDigest')
);
const NotFound = lazy(() => import('../layout/NotFound'));
const ResetPassword = lazy(() => import('../auth/ResetPassword'));
const ForgotPassword = lazy(() => import('../auth/ForgotPassword'));
const VerifyEmail = lazy(() => import('../auth/VerifyEmail'));
const EventDataGrid = lazy(() => import('../events/EventDataGrid'));
const EditHostsMatrix = lazy(() => import('../hosts/EditHostsMatrix'));
const EditAttendeesMatrix = lazy(
    () => import('../attendees/EditAttendeesMatrix')
);
const HostDeclinesFromEmailDigest = lazy(
    () => import('../events/HostDeclinesFromEmailDigest')
);
const FreeHostingGuide = lazy(() => import('../forms/FreeHostingGuide'));
const AttendeeEventDetails = lazy(
    () => import('../events/AttendeeEventDetails')
);
const PublicConcerts = lazy(() => import('../events/PublicConcerts'));

const TheRoutes = ({ app }) => {
    const theLocation = useLocation();
    const transitions = useTransition(theLocation, {
        keys: (loc) => loc.pathname,
        from: { opacity: 0, transform: 'scale(1.02)' },
        enter: { opacity: 1, transform: 'scale(1)' },
        leave: { opacity: 0, transform: 'scale(0.98)' },
        // config: config.stiff,
        config: { mass: 2, tension: 280, friction: 60 },
    });

    useEffect(() => {
        document.title = app.pageTitle;
    }, [app.pageTitle]);

    // Conditional smooth scroll-to-top on route changes:
    // - Do not animate on initial load
    // - Animate only if current scroll is beyond a threshold to avoid snap
    // - Returning to /artists via back/forward keeps the saved list position
    const ARTISTS_SCROLL_KEY = 'porchlight:artistsScroll';
    const navigationType = useNavigationType();
    const initialLoadRef = useRef(true);
    const prevPathnameRef = useRef(theLocation.pathname);
    const [, api] = useSpring(() => ({
        y: 0,
        config: { mass: 2, tension: 280, friction: 60 },
    }));
    useEffect(() => {
        const prevPathname = prevPathnameRef.current;
        const nextPathname = theLocation.pathname;
        prevPathnameRef.current = nextPathname;

        if (initialLoadRef.current) {
            initialLoadRef.current = false;
            return;
        }
        try {
            const root = document.getElementById('root');
            const getScrollY = () =>
                root
                    ? root.scrollTop
                    : window.scrollY ||
                      document.documentElement.scrollTop ||
                      document.body.scrollTop ||
                      0;
            const setScrollY = (val) => {
                if (root) root.scroll(0, val);
                else window.scrollTo(0, val);
            };
            if (prevPathname === '/artists' && nextPathname !== '/artists') {
                sessionStorage.setItem(
                    ARTISTS_SCROLL_KEY,
                    String(getScrollY())
                );
            }
            if (navigationType === 'POP' && nextPathname === '/artists') {
                api.stop();
                return;
            }
            if (navigationType === 'PUSH' && nextPathname === '/artists') {
                sessionStorage.removeItem(ARTISTS_SCROLL_KEY);
            }
            const current = getScrollY();
            const threshold = 200;
            if (current > threshold) {
                api.start({
                    from: { y: current },
                    to: { y: 0 },
                    reset: true,
                    onChange: (value) => {
                        setScrollY(value.value.y);
                    },
                });
            } else {
                setScrollY(0);
            }
        } catch (_) {}
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [theLocation.pathname, navigationType]);

    return (
        <section className="container">
            {transitions((style, theLocation) => {
                // console.log('theLocation', theLocation);
                return (
                    <animated.div style={style} className="animatedRoute">
                        <Suspense fallback={<Spinner />}>
                            <Routes
                                location={theLocation}
                                key={theLocation.pathname}
                            >
                                <Route
                                    path="/"
                                    element={
                                        <Page>
                                            <Landing />
                                        </Page>
                                    }
                                />
                                <Route
                                    path="/free-hosting-guide"
                                    element={
                                        <Page title="Free Hosting Guide">
                                            <FreeHostingGuide />
                                        </Page>
                                    }
                                />
                                <Route
                                    path="/register"
                                    element={
                                        <Page title="Register">
                                            <Register />
                                        </Page>
                                    }
                                />
                                <Route
                                    path="/reset-password"
                                    element={
                                        <Page title="Reset Password">
                                            <ResetPassword />
                                        </Page>
                                    }
                                />
                                <Route
                                    path="/verify-email"
                                    element={
                                        <Page title="Verify Email">
                                            <VerifyEmail />
                                        </Page>
                                    }
                                />
                                <Route
                                    path="/forgot-password"
                                    element={
                                        <Page title="Forgot Password">
                                            <ForgotPassword />
                                        </Page>
                                    }
                                />
                                <Route
                                    path="/unsubscribe/:id"
                                    element={
                                        <Page title="Unsubscribe">
                                            <UnsubscribeHostDigest />
                                        </Page>
                                    }
                                />
                                <Route
                                    path="/decline/:id"
                                    element={
                                        <Page title="Decline Event">
                                            <HostDeclinesFromEmailDigest />
                                        </Page>
                                    }
                                />
                                <Route
                                    path="/login"
                                    element={
                                        <Page title="Login">
                                            <Login />
                                        </Page>
                                    }
                                />
                                <Route
                                    path="/profiles"
                                    element={
                                        <Page title="Profiles">
                                            <Profiles />
                                        </Page>
                                    }
                                />
                                <Route
                                    path="/art"
                                    element={<Navigate to="/artists" replace />}
                                />
                                <Route
                                    path="/artist"
                                    element={<Navigate to="/artists" replace />}
                                />
                                <Route
                                    path="/artists"
                                    element={
                                        <Page title="Artists">
                                            <Artists />
                                        </Page>
                                    }
                                ></Route>
                                <Route
                                    path="/artists/:slug"
                                    // component={Artist}
                                    element={
                                        <Page title="Artist">
                                            <Artist />
                                        </Page>
                                    }
                                />
                                <Route
                                    path="/concerts"
                                    element={
                                        <Page title="Upcoming Concerts">
                                            <PublicConcerts />
                                        </Page>
                                    }
                                />
                                <Route
                                    path="/events/:id"
                                    element={
                                        <Page title="Event">
                                            <AttendeeEventDetails />
                                        </Page>
                                    }
                                />
                                {/* <Route
                                
                                path="/artists/:slug"
                                component={Artist}
                                // element={
                                //     <Page title="Profile">
                                //         <Artist />
                                //     </Page>
                                // }
                                /> */}

                                <Route
                                    path="/profile/:id"
                                    element={
                                        <Page title="Profile">
                                            <Profile />
                                        </Page>
                                    }
                                />
                                <Route
                                    path="/dashboard"
                                    element={
                                        <PrivateRoute title="Dashboard">
                                            <Dashboard />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/create-profile"
                                    element={
                                        <PrivateRoute title="Create Your Profile">
                                            <CreateProfile />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/edit-events"
                                    element={
                                        <BookingPrivateRoute title="Edit Events">
                                            <EventDataGrid />
                                        </BookingPrivateRoute>
                                    }
                                />
                                <Route
                                    path="/edit-hosts"
                                    element={
                                        <BookingPrivateRoute title="Edit Hosts">
                                            <EditHostsMatrix />
                                        </BookingPrivateRoute>
                                    }
                                />
                                <Route
                                    path="/edit-attendees"
                                    element={
                                        <BookingPrivateRoute title="Edit Attendees">
                                            <EditAttendeesMatrix />
                                        </BookingPrivateRoute>
                                    }
                                />
                                <Route
                                    path="/edit-artist-profile"
                                    element={
                                        <ArtistPrivateRoute title="Edit Your Artist Profile">
                                            <EditMyArtistProfile />
                                        </ArtistPrivateRoute>
                                    }
                                />

                                <Route
                                    path="/edit-host-profile"
                                    element={
                                        <PrivateRoute title="Edit Your Host Profile">
                                            <EditMyHostProfile />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/edit-attendee-profile"
                                    element={
                                        <PrivateRoute title="Edit Your Attendee Profile">
                                            <EditMyAttendeeProfile />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/edit-artists"
                                    element={
                                        <AdminPrivateRoute title="Edit Artists">
                                            <EditArtists />
                                        </AdminPrivateRoute>
                                    }
                                />
                                <Route
                                    path="/edit-users"
                                    element={
                                        <AdminPrivateRoute title="Edit Users">
                                            <EditUsers />
                                        </AdminPrivateRoute>
                                    }
                                />
                                <Route
                                    element={
                                        <Page title="Page Not Found">
                                            <NotFound />
                                        </Page>
                                    }
                                />
                            </Routes>
                        </Suspense>
                    </animated.div>
                );
            })}
        </section>
    );
};

// export default TheRoutes;

TheRoutes.propTypes = {
    app: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
    app: state.app,
});

// export default connect(mapStateToProps)(withRouter(TheRoutes)); //withRouter allows us to pass history objects
export default connect(mapStateToProps)(TheRoutes); //withRouter allows us to pass history objects
